import React from 'react';
import ChartLoader from '@/pages/dashboard/_charts/ChartLoader';
import DonutChart from '@/pages/dashboard/_charts/DonutChart';
import colors from 'tailwindcss/colors';
import {useProjectsContext} from "@/pages/dashboard/ProjectsContext";
import getStatusChartData from '@/pages/dashboard/_utils/getStatusChartData';

/**
 * ProjectStatusYear Component
 *
 * Displays a donut chart showing the distribution of projects by their current status.
 * The status categories include: Active, Closed, Draft, and Archived projects.
 *
 * @param {Object} props - Component props
 * @param {string} [props.subtitle] - Optional subtitle for the chart
 * @returns {JSX.Element} - A donut chart or loading indicator
 */
export default function ProjectStatusYear(props) {
  const {subtitle} = props;
  const {filteredProjects, isLoading} = useProjectsContext();

  // Show loading state while projects are being fetched
  if (isLoading) {
    return (
      <ChartLoader
        title="Project Status"
        subtitle={subtitle || 'For school year'}
      />
    );
  }

  // Get chart data by categorizing projects by their status
  // The getStatusChartData utility function processes filteredProjects and returns
  // series (counts) and labels (category names) for the chart
  const {series, labels} = getStatusChartData(filteredProjects);

  // Render the donut chart with color-coded status categories
  return (
    <DonutChart
      title="Project Status"
      subtitle={subtitle || 'For school year'}
      labels={labels}
      series={series}
      colors={[
        colors.emerald['400'],  // Green for active projects (current/ongoing)
        colors.red['400'],      // Red for closed projects (completed)
        colors.blue['400'],     // Blue for draft projects (in progress/pending)
        colors.yellow['400']    // Yellow for archived projects (inactive/historical)
      ]}
    />
  );
}
